// DTOs de C11 — configuración de turnos por planta.
//
// Modelo: 1 turno = (planta, numero 1-4) con hora_inicio + hora_fin.
// El flag cruza_medianoche se calcula automáticamente: TRUE si hora_fin < hora_inicio.
//
// Endpoints:
//   - GET    /api/coordinador/configuracion/turnos          — lista por scope
//   - POST   /api/coordinador/configuracion/turnos          — crear
//   - PATCH  /api/coordinador/configuracion/turnos/:id      — editar
//   - DELETE /api/coordinador/configuracion/turnos/:id      — desactivar (soft)
//   - GET    /api/coordinador/configuracion/turnos/:id/horario-semana — 7 días vigentes (R6, design horario-turno-dia)
//   - PUT    /api/coordinador/configuracion/turnos/:id/horario-semana — versiona los 7 días desde una fecha futura
import { z } from 'zod';
// HH:MM o HH:MM:SS — Postgres TIME admite ambas. Frontend trabaja con HH:MM.
const timeStringSchema = z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/, 'formato HH:MM esperado');
// =============================================================================
// Recursos
// =============================================================================
const plantaResumenSchema = z.object({
    id: z.string().uuid(),
    nombre: z.string(),
    codigo: z.string(),
});
// =============================================================================
// Turno
// =============================================================================
export const turnoConfigSchema = z.object({
    id: z.string().uuid(),
    planta_id: z.string().uuid(),
    planta_nombre: z.string(),
    planta_codigo: z.string(),
    numero: z.number().int().min(1).max(4),
    nombre: z.string(),
    /** HH:MM (sin segundos). */
    hora_inicio: timeStringSchema,
    /** HH:MM (sin segundos). */
    hora_fin: timeStringSchema,
    cruza_medianoche: z.boolean(),
    activo: z.boolean(),
    created_at: z.string(),
    updated_at: z.string(),
});
// =============================================================================
// Create / Update
// =============================================================================
export const turnoCreateSchema = z.object({
    planta_id: z.string().uuid(),
    numero: z.number().int().min(1).max(4),
    nombre: z.string().min(1).max(100),
    hora_inicio: timeStringSchema,
    hora_fin: timeStringSchema,
});
export const turnoUpdateSchema = z.object({
    nombre: z.string().min(1).max(100).optional(),
    hora_inicio: timeStringSchema.optional(),
    hora_fin: timeStringSchema.optional(),
    activo: z.boolean().optional(),
});
// =============================================================================
// Response: lista
// =============================================================================
export const coordinadorTurnosListSchema = z.object({
    user: z.object({
        id: z.string().uuid(),
        email: z.string().email(),
        nombre: z.string(),
        apellido: z.string(),
    }),
    tenant: z.object({ id: z.string().uuid(), nombre: z.string() }),
    recursos: z.object({
        plantas: z.array(plantaResumenSchema),
    }),
    turnos: z.array(turnoConfigSchema),
});
// =============================================================================
// Horario por día de la semana (R6, design horario-turno-dia §1-§3)
// =============================================================================
/** Fila de la respuesta GET: una por día ISO (1=lunes ... 7=domingo). */
export const horarioSemanaDiaSchema = z.object({
    dia_semana: z.number().int().min(1).max(7),
    no_opera: z.boolean(),
    hora_inicio: timeStringSchema.nullable(),
    hora_fin: timeStringSchema.nullable(),
    cruza_medianoche: z.boolean(),
    vigente_desde: z.string().date(),
});
export const horarioSemanaGetSchema = z.object({
    turno_id: z.string().uuid(),
    dias: z.array(horarioSemanaDiaSchema).length(7),
});
/** Fila de entrada del PUT — mismo shape que `thd_no_opera_horas` (mig 085). */
const horarioSemanaDiaInputSchema = z
    .object({
    dia_semana: z.number().int().min(1).max(7),
    no_opera: z.boolean(),
    hora_inicio: timeStringSchema.nullable().optional(),
    hora_fin: timeStringSchema.nullable().optional(),
})
    .superRefine((d, ctx) => {
    if (d.no_opera && (d.hora_inicio || d.hora_fin)) {
        ctx.addIssue({
            code: 'custom',
            message: 'un día "No opera" no admite hora_inicio/hora_fin',
            path: ['hora_inicio'],
        });
    }
    else if (!d.no_opera && (!d.hora_inicio || !d.hora_fin)) {
        ctx.addIssue({
            code: 'custom',
            message: 'hora_inicio y hora_fin son obligatorias cuando no_opera=false',
            path: ['hora_inicio'],
        });
    }
    else if (!d.no_opera && d.hora_inicio === d.hora_fin) {
        ctx.addIssue({
            code: 'custom',
            message: 'hora_inicio y hora_fin no pueden ser iguales',
            path: ['hora_fin'],
        });
    }
});
export const horarioSemanaPutSchema = z.object({
    dias: z
        .array(horarioSemanaDiaInputSchema)
        .length(7)
        .refine((dias) => new Set(dias.map((d) => d.dia_semana)).size === 7, {
        message: 'los 7 días ISO (1-7) deben estar presentes una sola vez cada uno',
    }),
    /** Fecha (YYYY-MM-DD) desde la que rige el nuevo horario — debe ser el próximo día operativo o posterior. */
    aplicar_desde: z.string().date(),
});
export const horarioSemanaPutResponseSchema = z.object({
    vigente_desde: z.string().date(),
    filas_creadas: z.number().int(),
    /** Programaciones (líderes + supervisores) futuras que caen en días ahora "No opera" — solo aviso, no se borran. */
    programaciones_afectadas_total: z.number().int(),
});
//# sourceMappingURL=coordinador_configuracion_turnos.js.map